import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, ArrowUp, Search, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NewThreadDialog } from "@/components/NewThreadDialog";
import { Avatar } from "@/components/Avatar";

export const ago = (iso) => formatDistanceToNow(new Date(iso), { addSuffix: true });

export const ReadOnlyNote = ({ user }) => (
  <p data-testid="read-only-note" className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
    <Lock className="h-4 w-4" />
    {user ? "Read-only for clients — only verified nail techs can post and reply." : <span>Only verified nail techs can post. <Link to="/join?role=tech" data-testid="read-only-join-link" className="underline">Join as a nail tech</Link></span>}
  </p>
);

const ThreadRow = ({ t }) => (
  <Link to={`/community/${t.id}`} data-testid="thread-row" className="flex gap-5 border-b border-[#EAE4DC] py-6 transition-colors hover:bg-white/60">
    <div className="flex w-12 shrink-0 flex-col items-center text-stone-500"><ArrowUp className="h-4 w-4" /><span className="font-mono text-sm">{t.upvotes}</span></div>
    <div className="min-w-0 flex-1">
      <span className="font-mono text-[10px] uppercase tracking-widest text-pink-600">{t.category}</span>
      <p className="mt-1 font-serif text-xl">{t.title}</p>
      <p className="mt-1 line-clamp-2 text-sm text-stone-600">{t.body}</p>
      <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
        <Avatar user={t.author} className="h-5 w-5" textClass="text-[9px]" />
        <span className="font-mono">@{t.author.username}</span> · {ago(t.created_at)}
        <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{t.reply_count}</span>
      </div>
    </div>
  </Link>
);

export default function Community() {
  const { user } = useAuth();
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: () => api.get("/meta").then((r) => r.data) });
  const { data: threads = [], refetch } = useQuery({ queryKey: ["threads", category, q], queryFn: () => api.get("/threads", { params: { category, q } }).then((r) => r.data) });
  const isTech = user?.role === "tech";
  const cats = ["", ...(meta?.categories || [])];
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-pink-600">Community board</p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Tips, help & questions</h1>
        </div>
        {isTech ? <NewThreadDialog categories={meta?.categories} onCreated={refetch} /> : <ReadOnlyNote user={user} />}
      </div>
      <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-4">
        <aside className="space-y-1">
          <label className="mb-4 flex items-center gap-2 rounded-xl border border-[#EAE4DC] bg-white px-4">
            <Search className="h-4 w-4 text-stone-400" />
            <input data-testid="community-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts" className="h-11 w-full bg-transparent text-sm outline-none" />
          </label>
          {cats.map((c) => (
            <button key={c || "all"} data-testid={`community-category-filter-${c || "all"}`} onClick={() => setCategory(c)}
              className={`block w-full rounded-xl px-4 py-2.5 text-left text-sm transition-colors ${category === c ? "bg-stone-900 text-white" : "hover:bg-white"}`}>
              {c || "All posts"}
            </button>
          ))}
        </aside>
        <section className="lg:col-span-3">
          {threads.map((t) => <ThreadRow key={t.id} t={t} />)}
          {threads.length === 0 && <p data-testid="community-empty" className="py-20 text-center text-stone-500">No posts here yet.</p>}
        </section>
      </div>
    </div>
  );
}
